import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import NotificationFilterSchema from "notifications/entities/notification/api/request-contracts/notification-filter-schema";
import NotificationOrderSchema from "notifications/entities/notification/api/request-contracts/notification-order-schema";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/entities/notification/api/request-contracts/notification-query";
import { z } from "zod";

export type NotificationQueryJSON = z.infer<typeof NotificationQuerySchema.NotificationQueryJSONZ>;

export type NotificationQueryParams = z.infer<typeof NotificationQuerySchema.NotificationQueryZ>;

export default class NotificationQuerySchema {
  public static readonly NotificationQueryJSONZ = z.object({
    filter: NotificationFilterSchema.NotificationFilterJSONZ,
    order: NotificationOrderSchema.NotificationOrderZ,
    pagination: PaginationSchema.PaginationZ,
  });

  public static readonly NotificationQueryZ = z.object({
    filter: NotificationFilterSchema.NotificationFilterZ,
    order: NotificationOrderSchema.NotificationOrderZ,
    pagination: PaginationSchema.PaginationZ,
  });

  public static validateJSON(values: unknown): NotificationQueryJSON {
    return parseOrThrow(NotificationQuerySchema.NotificationQueryJSONZ, values, {
      logError: true,
      schemaName: "NotificationQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is NotificationQueryJSON {
    try {
      NotificationQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: NotificationQueryJSON): NotificationQuery {
    const parsed = NotificationQuerySchema.validateJSON(json);

    const filterParameters = new NotificationFilterParameters({
      query: parsed.filter.query ?? undefined,
      boxType: parsed.filter.boxType ?? undefined,
      type: parsed.filter.type ?? undefined,
      from: parsed.filter.from ?? undefined,
      to: parsed.filter.to ?? undefined,
      description: parsed.filter.description ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filter.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filter.maxCreatedDate) ?? undefined,
      isUnread: parsed.filter.isUnread ?? undefined,
      isActionRequired: parsed.filter.isActionRequired ?? undefined,
      isMessageNotification: parsed.filter.isMessageNotification ?? undefined,
      topicId: Guid.fromJSON(parsed.filter.topicId) ?? undefined,
    });

    const orderParameters = parsed.order
      ? new NotificationOrderParameters({
          orderBy: parsed.order.orderBy!,
          orderDir: parsed.order.orderDir!,
        })
      : new NotificationOrderParameters({
          orderBy: NotificationOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = PaginationParameters.create({
      pageIndex: parsed.pagination.pageIndex,
      pageSize: parsed.pagination.pageSize,
    });

    return new NotificationQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }

  public static serialize(query: NotificationQuery): NotificationQueryJSON {
    const { filterParameters, orderParameters, paginationParameters } = query;

    const json: NotificationQueryJSON = {
      filter: {
        query: filterParameters.query,
        boxType: filterParameters.boxType,
        type: filterParameters.type,
        from: filterParameters.from,
        to: filterParameters.to,
        description: filterParameters.description,
        minCreatedDate: filterParameters.minCreatedDate?.toISOString(),
        maxCreatedDate: filterParameters.maxCreatedDate?.toISOString(),
        isUnread: filterParameters.isUnread,
        isActionRequired: filterParameters.isActionRequired,
        isMessageNotification: filterParameters.isMessageNotification,
        topicId: filterParameters.topicId ? filterParameters.topicId.value : undefined,
      },
      order: orderParameters
        ? {
            orderBy: orderParameters.orderBy,
            orderDir: orderParameters.orderDir,
          }
        : undefined,
      pagination: {
        pageIndex: paginationParameters.pageIndex,
        pageSize: paginationParameters.pageSize,
      },
    };

    return parseOrThrow(NotificationQuerySchema.NotificationQueryJSONZ, json, {
      logError: true,
      schemaName: "NotificationQuerySchema",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
